/**
 * Same-day dispatch cut-off countdown.
 *
 * Shopify has no cut-off-time setting — `Settings > Shipping and delivery >
 * Estimated delivery dates` only offers coarse fulfilment times ("Same
 * business day", "Next business day", ...) and applies its own internal
 * business-day cut-off that merchants cannot configure. So the 2pm promise
 * exists nowhere in the admin, and the storefront is the only place a customer
 * can ever be told about it. This file is that promise.
 *
 * Progressive enhancement: the snippet renders a correct static sentence on
 * its own. Everything here only ever makes that sentence more specific, so a
 * failure or an old browser degrades to "Order before 2pm ..." rather than to
 * nothing.
 */
(function () {
  'use strict';

  /** Cut-off in Europe/London wall-clock hours, 24h. Override per element with data-cutoff-hour. */
  var DEFAULT_CUTOFF_HOUR = 14;

  /**
   * England & Wales bank holidays, as Europe/London calendar dates.
   *
   * Substitute days matter and are easy to get wrong, so they are spelled out
   * rather than derived: 26 Dec 2026 falls on a Saturday (substitute Mon 28
   * Dec), and both 25 and 26 Dec 2027 fall at a weekend (substitutes Mon 27
   * and Tue 28 Dec). Easter moves too — Easter Sunday 2027 is 28 March.
   *
   * >>> RENEW BEFORE 2028. <<< Once the list runs out the countdown silently
   * treats bank holidays as ordinary working days and starts promising
   * deliveries that will not happen. Official list: gov.uk/bank-holidays
   */
  var BANK_HOLIDAYS = [
    // 2026
    '2026-08-31', // Summer
    '2026-12-25', // Christmas Day
    '2026-12-28', // Boxing Day (substitute)
    // 2027
    '2027-01-01', // New Year's Day
    '2027-03-26', // Good Friday
    '2027-03-29', // Easter Monday
    '2027-05-03', // Early May
    '2027-05-31', // Spring
    '2027-08-30', // Summer
    '2027-12-27', // Christmas Day (substitute)
    '2027-12-28', // Boxing Day (substitute)
  ];

  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /**
   * Read the current Europe/London wall clock.
   *
   * The customer's own timezone is irrelevant and actively misleading — a
   * buyer in Spain must see the same deadline as one in Manchester, because
   * the warehouse is in England. Everything below is London wall-clock time.
   */
  function londonNow() {
    var parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    var out = {};
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].type !== 'literal') out[parts[i].type] = parseInt(parts[i].value, 10);
    }

    return {
      year: out.year,
      month: out.month,
      day: out.day,
      // Some engines render midnight as hour 24 under hour12:false.
      hour: out.hour === 24 ? 0 : out.hour,
      minute: out.minute,
      second: out.second,
    };
  }

  function toIso(d) {
    return (
      d.year + '-' + String(d.month).padStart(2, '0') + '-' + String(d.day).padStart(2, '0')
    );
  }

  /** Day of week for a calendar date, via UTC so no timezone can shift it. */
  function weekdayOf(d) {
    return new Date(Date.UTC(d.year, d.month - 1, d.day)).getUTCDay();
  }

  function isWorkingDay(d) {
    var dow = weekdayOf(d);
    if (dow === 0 || dow === 6) return false;
    return BANK_HOLIDAYS.indexOf(toIso(d)) === -1;
  }

  function addDays(d, n) {
    var t = new Date(Date.UTC(d.year, d.month - 1, d.day));
    t.setUTCDate(t.getUTCDate() + n);
    return { year: t.getUTCFullYear(), month: t.getUTCMonth() + 1, day: t.getUTCDate() };
  }

  function nextWorkingDay(d) {
    var next = addDays(d, 1);
    // Bounded so a malformed holiday list can never spin forever.
    for (var i = 0; i < 30 && !isWorkingDay(next); i++) next = addDays(next, 1);
    return next;
  }

  /**
   * Minutes remaining until the cut-off, in wall-clock terms.
   *
   * Deliberately arithmetic on the London clock rather than on UTC instants:
   * British Summer Time changes at 01:00, so it can never fall between "now"
   * and a same-day 14:00 cut-off, and wall-clock subtraction is therefore
   * exact without any offset handling.
   */
  function minutesUntil(now, cutoffHour) {
    return cutoffHour * 60 - (now.hour * 60 + now.minute);
  }

  function formatRemaining(minutes) {
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    if (h > 0) return h + 'h ' + m + 'm';
    return m + 'm';
  }

  /**
   * The sentence, as an ordered list of parts rather than a string.
   *
   * Returning parts instead of HTML keeps the rendering path free of
   * innerHTML. Nothing here is user-controlled, but a countdown is not worth
   * an HTML-injection sink existing at all.
   *
   * @returns {Array<{text: string, strong?: boolean}>}
   */
  function messageParts(cutoffHour) {
    var now = londonNow();
    var openToday = isWorkingDay(now);
    var remaining = minutesUntil(now, cutoffHour);

    if (openToday && remaining > 0) {
      return [
        { text: 'Order within ' },
        { text: formatRemaining(remaining), strong: true },
        { text: ' for next-working-day delivery' },
      ];
    }

    // Past the cut-off, or a weekend or bank holiday: dispatch slips to the
    // next working day, and delivery to the one after that.
    var dispatch = openToday ? nextWorkingDay(now) : nextWorkingDay(addDays(now, -1));
    var delivery = nextWorkingDay(dispatch);

    return [
      { text: 'Order now — dispatched ' },
      { text: WEEKDAYS[weekdayOf(dispatch)], strong: true },
      { text: ', delivered ' },
      { text: WEEKDAYS[weekdayOf(delivery)], strong: true },
    ];
  }

  function render(el) {
    var cutoffHour = parseInt(el.getAttribute('data-cutoff-hour'), 10);
    if (isNaN(cutoffHour)) cutoffHour = DEFAULT_CUTOFF_HOUR;

    var body = el.querySelector('[data-delivery-cutoff-text]');
    if (!body) return;

    var parts = messageParts(cutoffHour);
    var frag = document.createDocumentFragment();

    for (var i = 0; i < parts.length; i++) {
      if (parts[i].strong) {
        var strong = document.createElement('strong');
        strong.textContent = parts[i].text;
        frag.appendChild(strong);
      } else {
        frag.appendChild(document.createTextNode(parts[i].text));
      }
    }

    body.textContent = '';
    body.appendChild(frag);
  }

  function renderAll() {
    var nodes = document.querySelectorAll('[data-delivery-cutoff]');
    for (var i = 0; i < nodes.length; i++) render(nodes[i]);
  }

  function start() {
    if (typeof Intl === 'undefined' || !Intl.DateTimeFormat) return; // keep the static fallback
    renderAll();
    // 30s is enough for minute-granularity copy and avoids a per-second
    // reflow on a page that is mostly images.
    setInterval(renderAll, 30000);
    // The cart drawer injects its markup after a cart update.
    document.addEventListener('cart:refresh', renderAll);
    document.addEventListener('cart:updated', renderAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
